import { useCallback, useState } from "react";
import { useCadastroCoresListaPagination } from "../hooks/CadastroCores_lista/pagination";
import { initialCadastroCoresListaState, useCadastroCoresListaFilters } from "../hooks/CadastroCores_lista/filters";
import { CadastroCores } from "../../domain/models/cadastro_cores";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { CadastroCoresForm } from "../schemas/CadastroCores_lista/cadastro_cores";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";

/**
 * LoadCadastroCoresFuncState
 */

type LoadCadastroCoresFuncState = {
  cor?: string | null;
  dataDe?: Date | null;
  dataAte?: Date | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useCadastroCoresListaViewModel
 */

export const useCadastroCoresListaViewModel = () => {
  const {
    equipamentoMedicao: {
      createCadastroCoresUseCase,
      getAllCadastroCoresUseCase,
      updateCadastroCoresUseCase,
      deleteCadastroCoresUseCase,
    },
  } = useDependencyInjection();

  const debouncedHandler = useCreateDebounceHandler(750);
  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [cadastroCoresModal, setCadastroCoresModal] = useState(false);
  const [cadastroCoresDeleteModal, setCadastroCoresDeleteModal] = useState(false);

  const [cadastroCoresLista, setCadastroCoresLista] = useState<CadastroCores[]>();
  const [selectedCadastroCores, setSelectedCadastroCores] = useState<CadastroCores>();

  const filters = useCadastroCoresListaFilters();
  const pagination = useCadastroCoresListaPagination();

  /**
   * Funções de carregamento
   */

  const loadCadastroCores = useCallback(async (fs?: LoadCadastroCoresFuncState) => {
    try {
      const filteredCor = evaluateParamValue(fs?.cor, filters.cor)
      const filteredDataDe = evaluateParamValue(fs?.dataDe, filters.dataDe);
      const filteredDataAte = evaluateParamValue(fs?.dataAte, filters.dataAte);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllCadastroCoresUseCase.getAllCadastroCores(
        filteredCor,
        filteredDataDe,
        filteredDataAte,
        filteredPage,
        filteredRowsPerPage,
      );

      setCadastroCoresLista(getAllCadastroCoresUseCase.items);
      pagination.setTotalItems(getAllCadastroCoresUseCase.totalItems);
    } catch (e: unknown) {
      setCadastroCoresLista(undefined);
      throw new Error('Falha ao buscar dados de cores!');
    }
  }, [
    filters,
    pagination,
    getAllCadastroCoresUseCase,
  ]);

  /**
   * useEffects
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCadastroCores();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados iniciais da página!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCadastroCores();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados iniciais da página!')[0]);
    } finally {
      setLoading(false);
    }
  }, [
    loadCadastroCores,
    pushErrorNotification,
  ]);

  const handleCadastroCoresModalOpenState = useCallback((idCadastroCor: number | null, openState: boolean) => {
    if (openState) {
      if (cadastroCoresLista && idCadastroCor) {
        setSelectedCadastroCores(cadastroCoresLista.find(x => x.idCadastroCor === idCadastroCor));
      } else {
        setSelectedCadastroCores(undefined);
      }
    }
    setCadastroCoresModal(openState);
  }, [
    cadastroCoresLista,
  ]);

  const handleCadastroCoresSubmit = useCallback(async (form: CadastroCoresForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedCadastroCores) {
        await updateCadastroCoresUseCase.updateCadastroCores(
          selectedCadastroCores.idCadastroCor,
          form.dataInicio,
          form.dataTermino,
          form.idCor,
          form.cor,
          form.corValor,
        );
      } else {
        await createCadastroCoresUseCase.createCadastroCores(
          form.dataInicio,
          form.dataTermino,
          form.idCor,
          form.cor,
          form.corValor,
        );
      }

      setCadastroCoresModal(false);
      setSelectedCadastroCores(undefined);

      await loadCadastroCores();
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao salvar registro!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedCadastroCores,
    updateCadastroCoresUseCase,
    createCadastroCoresUseCase,
    loadCadastroCores,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleCadastroCoresDeleteModalOpenState = useCallback((idCadastroCor: number | null, openState: boolean) => {
    if (openState) {
      if (cadastroCoresLista && idCadastroCor) {
        setSelectedCadastroCores(cadastroCoresLista.find(x => x.idCadastroCor === idCadastroCor));
      } else {
        setSelectedCadastroCores(undefined);
      }
    }
    setCadastroCoresDeleteModal(openState);
  }, [
    cadastroCoresLista,
  ]);

  const handleCadastroCoresDeleteSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedCadastroCores) {
        await deleteCadastroCoresUseCase
          .deleteCadastroCores(selectedCadastroCores.idCadastroCor);
      }

      setCadastroCoresDeleteModal(false);
      setSelectedCadastroCores(undefined);

      await loadCadastroCores();
      pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao excluir registro!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedCadastroCores,
    deleteCadastroCoresUseCase,
    loadCadastroCores,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useCadastroCoresListaPagination.setState({ page: page + 1 });
      await loadCadastroCores({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadCadastroCores,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useCadastroCoresListaPagination.setState({ page: 1, rowsPerPage });
      await loadCadastroCores({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadCadastroCores,
    pushErrorNotification,
  ]);

  const handleDataChange = useDebouncedCallback(debouncedHandler, (de: Date | null, ate: Date | null) => {
    useCadastroCoresListaFilters.setState({ dataDe: de, dataAte: ate });
    return async () => {
      try {
        setLoading(true);
        useCadastroCoresListaPagination.setState({ page: 1 });
        await loadCadastroCores({ dataDe: de, dataAte: ate, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadCadastroCores,
    pushErrorNotification,
  ]);

  const handleCorChange = useDebouncedCallback(debouncedHandler, (cor: string | null) => {
    useCadastroCoresListaFilters.setState({ cor });
    return async () => {
      try {
        setLoading(true);
        useCadastroCoresListaPagination.setState({ page: 1 });
        await loadCadastroCores({ cor, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadCadastroCores,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useCadastroCoresListaFilters.setState({ ...initialCadastroCoresListaState });
      useCadastroCoresListaPagination.setState({ page: 1 });
      await loadCadastroCores({ ...initialCadastroCoresListaState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da página!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadCadastroCores,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    cadastroCoresModal,
    cadastroCoresDeleteModal,
    cadastroCoresLista: cadastroCoresLista ?? [],
    selectedCadastroCores: selectedCadastroCores ?? null,
    filters,
    pagination,
    handleReload,
    handleCadastroCoresModalOpenState,
    handleCadastroCoresSubmit,
    handleCadastroCoresDeleteModalOpenState,
    handleCadastroCoresDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleDataChange,
    handleCorChange,
    handleClearFilters,
  };
}